import { useMemo } from "react";
import {
  AssessmentQuestionComment,
  AssessmentType,
  CalibrationNodeFragment,
  RangeAssessmentQuestionNode,
} from "types/graphql-schema";

import { isEmptyValue } from "@components/wysiwyg/helpers";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";

import AssessmentQuestionRangeOptions from "../assessment-question-range-options";
import QuestionContextButton from "./question-context-button";
import { AssessmentAnswer, AssessmentAnswerWithResponderAndId } from "./types";

const RangeAssessmentQuestionContent = ({
  answer,
  otherParticipantsAnswers,
  question,
  disabled,
  onlyRangeOptionsDisabled,
  calibrations,
  forceHideComment = false,
  onChangeAnswer,
  assessmentType,
}: {
  answer?: { integerAnswer?: number | null; comment?: string | null } | null;
  otherParticipantsAnswers?: AssessmentAnswerWithResponderAndId[];
  forceHideComment?: boolean;
  question: Pick<
    RangeAssessmentQuestionNode,
    | "id"
    | "labelDescriptions"
    | "labels"
    | "startValue"
    | "endValue"
    | "comment"
  >;
  disabled?: boolean;
  onlyRangeOptionsDisabled?: boolean;
  calibrations?: CalibrationNodeFragment[];
  onChangeAnswer?: (newAnswer: Partial<AssessmentAnswer>) => void;
  assessmentType?: AssessmentType | null;
}) => {
  const comment = answer?.comment ?? "{}";

  const initialAnswer = useMemo(() => {
    const filteredCalibrations = calibrations?.filter(
      (calibration) => !calibration.isSuggestion,
    );
    if (!filteredCalibrations || filteredCalibrations.length === 0) {
      return answer?.integerAnswer;
    }
    return filteredCalibrations[filteredCalibrations.length - 1]
      .prevIntegerAnswer;
  }, [calibrations, answer]);

  return (
    <>
      <AssessmentQuestionRangeOptions
        answer={answer?.integerAnswer}
        answerHasChanged={answer?.integerAnswer !== initialAnswer}
        otherParticipantsAnswers={otherParticipantsAnswers}
        disabled={disabled || onlyRangeOptionsDisabled}
        labels={question.labels}
        labelDescriptions={question.labelDescriptions}
        startValue={question.startValue}
        endValue={question.endValue}
        onClickAnswer={(answer) => {
          if (!onChangeAnswer) {
            return;
          }
          onChangeAnswer({
            integerAnswer: answer,
          });
        }}
      />
      {!forceHideComment &&
        question.comment !== AssessmentQuestionComment.NoComment &&
        answer &&
        (!disabled || !isEmptyValue(answer.comment)) && (
          <div className="mt-4 flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="text-gray-500 text-xs uppercase font-semibold">
                Comment{" "}
                {question.comment === AssessmentQuestionComment.Required
                  ? "*"
                  : ""}
              </div>
            </div>
            <TextareaWysiwyg
              editable={!disabled}
              placeholder="Add a comment..."
              className="bg-white max-w-none"
              value={comment}
              onChangeValue={(comment) => {
                if (!onChangeAnswer) {
                  return;
                }
                onChangeAnswer({
                  comment,
                });
              }}
            />
            {!disabled && (
              <QuestionContextButton
                questionId={question.id}
                assessmentType={assessmentType}
              />
            )}
          </div>
        )}
    </>
  );
};

export default RangeAssessmentQuestionContent;
